import { useId } from 'react';
import { CalendarDays, Check, MapPin, User } from 'lucide-react';
import { Alert, Badge, Button, useToast } from '@waymail/ui';
import type { ConviteDaApi } from '../lib/api';
import { quandoDoConvite, useAcaoDoConvite, type AcaoDoConvite } from '../lib/calendario';

const RESPOSTAS: { acao: AcaoDoConvite; rotulo: string; feito: string }[] = [
  { acao: 'aceitar', rotulo: 'Aceitar', feito: 'Convite aceito.' },
  { acao: 'talvez', rotulo: 'Talvez', feito: 'Resposta "talvez" enviada.' },
  { acao: 'recusar', rotulo: 'Recusar', feito: 'Convite recusado.' },
];

/**
 * Convite de reuniao acima do corpo. Tudo aqui e texto vindo do remetente,
 * exibido como texto: nada vira HTML nem link.
 */
export function CartaoDeConvite({
  messageId,
  convite: c,
}: {
  messageId: string;
  convite: ConviteDaApi;
}) {
  const titulo = useId();
  const acao = useAcaoDoConvite(messageId);
  const toast = useToast();
  const cancelado = c.metodo === 'CANCEL';
  const pedeResposta = c.metodo === 'REQUEST' && c.organizador?.email != null;
  const organizador = c.organizador
    ? c.organizador.nome && c.organizador.email
      ? `${c.organizador.nome} <${c.organizador.email}>`
      : c.organizador.nome || c.organizador.email
    : null;

  function executar(a: AcaoDoConvite, feito: string) {
    acao.mutate(a, {
      onSuccess: () => toast({ title: feito }),
      onError: (e) => toast({ title: e.message, tone: 'error' }),
    });
  }

  const emCurso = (a: AcaoDoConvite) => acao.isPending && acao.variables === a;
  const temBotoes =
    c.metodo !== 'REPLY' && (pedeResposta || (cancelado ? c.jaNaAgenda : !c.jaNaAgenda));

  return (
    <section aria-labelledby={titulo} className="mt-5 rounded-lg border border-subtle px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <CalendarDays size={16} strokeWidth={1.8} aria-hidden="true" className="text-tertiary" />
        <h2 id={titulo} className="min-w-0 flex-1 text-[14px] font-semibold text-primary">
          {c.titulo}
        </h2>
        {cancelado && <Badge tone="red">Cancelado</Badge>}
        {c.metodo === 'REPLY' && <Badge tone="gray">Resposta a convite</Badge>}
        {!cancelado && c.jaNaAgenda && (
          <Badge tone="green">
            <Check size={12} strokeWidth={2.2} aria-hidden="true" />
            Já está no calendário
          </Badge>
        )}
      </div>

      <dl className="mt-2 space-y-1 text-[13px] text-secondary">
        <div className="flex gap-2">
          <dt className="sr-only">Quando</dt>
          <dd>{quandoDoConvite(c)}</dd>
        </div>
        {c.local !== null && (
          <div className="flex items-center gap-2">
            <dt>
              <MapPin size={14} strokeWidth={1.8} aria-hidden="true" />
              <span className="sr-only">Local</span>
            </dt>
            <dd className="min-w-0 break-words">{c.local}</dd>
          </div>
        )}
        {organizador !== null && (
          <div className="flex items-center gap-2">
            <dt>
              <User size={14} strokeWidth={1.8} aria-hidden="true" />
              <span className="sr-only">Organizador</span>
            </dt>
            <dd className="min-w-0 break-words">{organizador}</dd>
          </div>
        )}
      </dl>

      {cancelado && (
        <p className="mt-2 text-[13px] text-secondary">O organizador cancelou este evento.</p>
      )}
      {c.repete && !cancelado && (
        <p className="mt-2 text-[12px] text-tertiary">
          {c.serie
            ? `Evento repetido: a série inteira ${c.jaNaAgenda ? 'está' : 'entra'} no calendário.`
            : `Evento repetido: só a primeira ocorrência ${c.jaNaAgenda ? 'foi adicionada' : 'será adicionada'} ao calendário.`}
        </p>
      )}
      {c.fusoNaoReconhecido && (
        <Alert tone="warning" className="mt-2">
          Fuso horário do convite não reconhecido: o horário foi lido como UTC e pode estar errado.
        </Alert>
      )}

      {temBotoes && (
        <div className="mt-3 flex flex-wrap gap-2">
          {pedeResposta &&
            RESPOSTAS.map((r) => (
              <Button
                key={r.acao}
                size="sm"
                variant={r.acao === 'aceitar' ? 'primary' : 'ghost'}
                disabled={acao.isPending}
                loading={emCurso(r.acao)}
                onClick={() => executar(r.acao, r.feito)}
              >
                {r.rotulo}
              </Button>
            ))}
          {cancelado
            ? c.jaNaAgenda && (
                <Button
                  size="sm"
                  variant="danger"
                  disabled={acao.isPending}
                  loading={emCurso('agenda')}
                  onClick={() => executar('agenda', 'Removido do calendário.')}
                >
                  Remover do calendário
                </Button>
              )
            : !c.jaNaAgenda && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={acao.isPending}
                  loading={emCurso('agenda')}
                  onClick={() => executar('agenda', 'Adicionado ao calendário.')}
                >
                  Adicionar ao calendário
                </Button>
              )}
        </div>
      )}
    </section>
  );
}
